import { themeVars } from '@customerio/pluma-components/css/theme-tokens';

export type StrokeSettings = {
  color: string;
  dasharray: string;
  width: string;
};

export type PathSettings = {
  id: string;
  d: string;
  stroke: StrokeSettings;
};

export type PathDotSettings = {
  id: string;
  x: number;
  y: number;
  pathId: string;
  color: string;
};

type Point = { x: number; y: number };
type PathEdgeCoordinates = { start: Point; end: Point };

const PATH_SAFETY_MARGIN = 10;

const StrokeColor = {
  purple: 'purple',
  orange: 'orange',
  yellow: 'yellow',
  blue: 'blue',
  gray: 'gray',
} as const;
type StrokeColor = (typeof StrokeColor)[keyof typeof StrokeColor];

const STROKE_SETTINGS = {
  [StrokeColor.purple]: { color: themeVars.palette['purple-200'], dasharray: '0 0', width: '3' },
  [StrokeColor.orange]: { color: themeVars.palette['orange-200'], dasharray: '0 0', width: '3' },
  [StrokeColor.yellow]: { color: themeVars.palette['yellow-200'], dasharray: '0 0', width: '3' },
  [StrokeColor.blue]: { color: themeVars.palette['blue-200'], dasharray: '0 0', width: '3' },
  [StrokeColor.gray]: { color: themeVars.palette['grey-200'], dasharray: '0 0', width: '3' },
} satisfies Record<StrokeColor, StrokeSettings>;

function generateStrokeSettings(type: string): StrokeSettings {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return STROKE_SETTINGS[type as StrokeColor] ?? STROKE_SETTINGS[StrokeColor.gray];
}

/**
 * Builds a `<path>` "d" string as a Bezier curve for the first half followed by
 * a smooth curve extending it for the second half.
 * https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#cubic_b%C3%A9zier_curve
 */
function generatePathString(startCoordinates: Point, endCoordinates: Point): string {
  const startX = startCoordinates.x;
  const startY = startCoordinates.y;
  const endX = endCoordinates.x;
  const endY = endCoordinates.y;

  const horizontalBezierControlOffset = (endX - startX) / 3;

  const moveCommand = `M${startX},${startY}`;

  const bezierControlPoints = {
    x1: Math.round(startX + horizontalBezierControlOffset),
    y1: Math.round(startY),
    // 20 is an extra offset found experimentally, for visual balance.
    x2: Math.round(startX + (endX - startX) / 2 - 20),
    y2: Math.round(startY),
    x: Math.round(startX + (endX - startX) / 2),
    y: Math.round(startY + (endY - startY) / 2),
  };
  const bezierCommand = `C${bezierControlPoints.x1},${bezierControlPoints.y1} ${bezierControlPoints.x2},${bezierControlPoints.y2} ${bezierControlPoints.x},${bezierControlPoints.y}`;

  const smoothCurveControlPoints = {
    x2: Math.round(startX + horizontalBezierControlOffset * 2),
    y2: Math.round(endY),
    x: Math.round(endX),
    y: Math.round(endY),
  };
  const smoothCurveCommand = `S${smoothCurveControlPoints.x2},${smoothCurveControlPoints.y2} ${smoothCurveControlPoints.x},${smoothCurveControlPoints.y}`;

  return `${moveCommand} ${bezierCommand} ${smoothCurveCommand}`;
}

function generatePathEdgeCoordinates({
  containerRect,
  elementRect,
  targetElementRect,
  sourceYOffset,
  targetYOffset,
  horizontalLine,
}: {
  containerRect: DOMRect;
  elementRect: DOMRect;
  targetElementRect: DOMRect;
  sourceYOffset: number;
  targetYOffset: number;
  horizontalLine: boolean;
}): PathEdgeCoordinates {
  const xOffset = containerRect.left;
  const yOffset = containerRect.top;

  const start = {
    x: Math.round(elementRect.right - xOffset - PATH_SAFETY_MARGIN),
    y: Math.round(elementRect.top + elementRect.height / 2 - yOffset + sourceYOffset),
  };

  const end = {
    x: Math.round(targetElementRect.left - xOffset + PATH_SAFETY_MARGIN),
    y: horizontalLine
      ? start.y
      : Math.round(targetElementRect.top + targetElementRect.height / 2 - yOffset + targetYOffset),
  };

  return { start, end };
}

interface GeneratedFlow {
  paths: PathSettings[];
  pathDots: PathDotSettings[];
}

/**
 * Reads the `.js-flow-item` cards inside `container` and their `data-flow-*`
 * attributes, then produces the connecting bezier paths and the animated dots
 * that ride the enabled ones.
 */
export function generatePathsAndDots(container: HTMLElement): GeneratedFlow {
  const containerRect = container.getBoundingClientRect();

  const paths: PathSettings[] = [];
  const pathDots: PathDotSettings[] = [];

  const flowElements = Array.from(container.querySelectorAll('.js-flow-item'));
  const elementRectMap = new Map<string, DOMRect>();

  // Reverse so the first flow renders on top of lower ones.
  flowElements.reverse().forEach((element, eIndex) => {
    const id = element.getAttribute('data-flow-id') ?? '';
    const targets = element.getAttribute('data-flow-targets') ?? '';
    const horizontalLine = element.hasAttribute('data-flow-horizontal-line');

    // Targets are a ";"-separated list of
    // "targetId,enabledEnumString,strokeColor,sourceYOffset,targetYOffset".
    const flowTargets = targets.split(';').filter((item) => item.trim() !== '');

    flowTargets.forEach((target, tIndex) => {
      const [targetId, enabled = 'disabled', color = StrokeColor.gray, sourceYOffset = '0', targetYOffset = '0'] =
        target.split(',');
      if (targetId == null) {
        return;
      }
      const targetElement = container.querySelector(`.js-flow-item[data-flow-id="${targetId}"]`);
      if (targetElement == null) {
        return;
      }

      const isEnabled = enabled === 'enabled';
      const idBase = `flow_item_${eIndex}_${tIndex}`;

      const elementRect = elementRectMap.get(id) ?? element.getBoundingClientRect();
      elementRectMap.set(id, elementRect);
      const targetElementRect = elementRectMap.get(targetId) ?? targetElement.getBoundingClientRect();
      elementRectMap.set(targetId, targetElementRect);

      const stroke = generateStrokeSettings(color);

      const coordinates = generatePathEdgeCoordinates({
        containerRect,
        elementRect,
        targetElementRect,
        sourceYOffset: Number(sourceYOffset),
        targetYOffset: Number(targetYOffset),
        horizontalLine,
      });

      const path: PathSettings = {
        id: `${idBase}_path`,
        d: generatePathString(coordinates.start, coordinates.end),
        stroke,
      };

      if (isEnabled) {
        paths.push(path);
        pathDots.push({
          id: `${idBase}_dot`,
          x: coordinates.start.x,
          y: coordinates.start.y,
          pathId: path.id,
          color: stroke.color,
        });
      } else {
        // Push disabled flow lines under the enabled ones.
        paths.unshift(path);
      }
    });
  });

  return { paths, pathDots };
}
